import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Calendar2Week,
  Check2,
  ShieldCheck,
} from "react-bootstrap-icons";

import { useAuthContext } from "../../hooks/useAuthContext";
import { ApiConnectionString } from "../../constants/ApiConntection";
import LoadingSpinner from "../../components/LoadingSpinner";
import PublicLayout from "../../components/public/PublicLayout";

import "./LoginPage.css";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMode, setForgotMode] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const [loading, setLoading] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const [error, setError] = useState("");
  const [forgotError, setForgotError] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");

  const { dispatch, user } = useAuthContext();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();

    setError("");
    setForgotMessage("");

    if (!email.trim() || !password) {
      setError("Please enter your email and password.");
      return;
    }

    setLoading(true);

    const loginDetails = {
      email: email.trim().toLowerCase(),
      password,
      rememberMe,
    };

    try {
   const res = await axios.post(
  `${ApiConnectionString}/api/login/`,
  loginDetails,
  {
    withCredentials: true,
  }
);

      if (res.status === 200) {
        dispatch({
          type: "LOGIN",
          payload: res.data,
        });

        navigate("/");
      }
    } catch (err) {
      if (err.response?.status === 400) {
        const data = err.response.data;
        setError(Array.isArray(data) ? data[0] : data);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const openForgotPassword = () => {
    setForgotMode(true);
    setForgotEmail(email);
    setError("");
    setForgotError("");
    setForgotMessage("");
  };

  const backToLogin = () => {
    setForgotMode(false);
    setForgotError("");
    setForgotMessage("");
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    setForgotError("");
    setForgotMessage("");

    if (!forgotEmail.trim()) {
      setForgotError("Please enter your email address.");
      return;
    }

    try {
      setForgotLoading(true);

      await axios.post(
        `${ApiConnectionString}/api/therapist/ForgotPassword?email=${encodeURIComponent(
          forgotEmail.trim().toLowerCase()
        )}`
      );

      setForgotMessage(
        "If this email address is registered, a password reset link has been sent."
      );
    } catch (err) {
      if (err.response?.status === 429) {
        setForgotError(
          "Too many password reset requests. Please try again later."
        );
      } else if (err.response?.data) {
        const data = err.response.data;
        setForgotError(Array.isArray(data) ? data[0] : data);
      } else {
        setForgotError("Something went wrong. Please try again.");
      }
    } finally {
      setForgotLoading(false);
    }
  };

  const testUpdate = async () => {
    try {
      const res = await axios.get(`${ApiConnectionString}/update`);
      console.log("Update response:", res.data);
      alert("Request successful");
    } catch (err) {
      console.error("Update failed:", err);
      alert("Request failed");
    }
  };

  if (loading) {
    return (
      <div className="login-loading">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="login-site">
      <PublicLayout>
        <main className="login-main">
          <div className="login-container login-layout">
            <section className="login-form-column">
              <div className="login-card">
                <button type="button" onClick={testUpdate}>
                  TEMP: Test Update API
                </button>

                {!forgotMode ? (
                  <>
                    <span className="login-card-label">
                      Therapist login
                    </span>

                    <div className="login-heading">
                      <h1>Welcome back</h1>

                      <p>
                        Log in to manage your appointments, clients and
                        availability.
                      </p>
                    </div>

                    {error && (
                      <div className="alert alert-danger" role="alert">
                        {error}
                      </div>
                    )}

                    <form onSubmit={handleLogin}>
                      <div className="login-field">
                        <label htmlFor="login-email">
                          Email address
                        </label>

                        <input
                          id="login-email"
                          type="email"
                          className="form-control"
                          placeholder="you@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          autoComplete="email"
                        />
                      </div>

                      <div className="login-field">
                        <label htmlFor="login-password">
                          Password
                        </label>

                        <div className="login-password-wrap">
                          <input
                            id="login-password"
                            type={showPassword ? "text" : "password"}
                            className="form-control"
                            placeholder="Enter your password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="current-password"
                          />

                          <button
                            type="button"
                            className="login-show-btn"
                            onClick={() =>
                              setShowPassword((current) => !current)
                            }
                          >
                            {showPassword ? "Hide" : "Show"}
                          </button>
                        </div>
                      </div>

                      <div className="login-options">
                        <label className="remember-option">
                          <input
                            type="checkbox"
                            checked={rememberMe}
                            onChange={(e) =>
                              setRememberMe(e.target.checked)
                            }
                          />

                          <span>Remember me</span>
                        </label>

                        <button
                          type="button"
                          className="forgot-link"
                          onClick={openForgotPassword}
                        >
                          Forgot password?
                        </button>
                      </div>

                      <button
                        type="submit"
                        className="login-submit-btn"
                        disabled={loading}
                      >
                        Log in
                        <ArrowRight />
                      </button>
                    </form>

                    <div className="login-mobile-signup">
                      <span>New to Therapy Diary App?</span>

                      <Link to="/signup">
                        Start your 1-month free trial
                      </Link>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="login-card-label">
                      Account recovery
                    </span>

                    <div className="login-heading">
                      <h1>Reset your password</h1>

                      <p>
                        Enter your email address and we’ll send you a reset
                        link if an account exists.
                      </p>
                    </div>

                    {forgotError && (
                      <div className="alert alert-danger" role="alert">
                        {forgotError}
                      </div>
                    )}

                    {forgotMessage && (
                      <div className="alert alert-success" role="alert">
                        {forgotMessage}
                      </div>
                    )}

                    <form onSubmit={handleForgotPassword}>
                      <div className="login-field">
                        <label htmlFor="forgot-email">
                          Email address
                        </label>

                        <input
                          id="forgot-email"
                          type="email"
                          className="form-control"
                          placeholder="you@example.com"
                          value={forgotEmail}
                          onChange={(e) =>
                            setForgotEmail(e.target.value)
                          }
                          autoComplete="email"
                        />
                      </div>

                      <button
                        type="submit"
                        className="login-submit-btn"
                        disabled={forgotLoading}
                      >
                        {forgotLoading
                          ? "Sending..."
                          : "Send reset link"}

                        {!forgotLoading && <ArrowRight />}
                      </button>

                      <button
                        type="button"
                        className="forgot-back-btn"
                        onClick={backToLogin}
                        disabled={forgotLoading}
                      >
                        Back to login
                      </button>
                    </form>
                  </>
                )}
              </div>
            </section>

            <aside className="login-signup-panel">
              <div className="login-signup-content">
                <span className="login-signup-eyebrow">
                  Simple software for therapists
                </span>

                <h2>
                  Spend less time managing admin and more time with your
                  clients.
                </h2>

                <p>
                  Keep your diary, clients and availability organised in one
                  calm, straightforward place.
                </p>

                <ul className="login-benefits">
                  <li>
                    <span>
                      <Calendar2Week />
                    </span>

                    <div>
                      <strong>A clear appointment diary</strong>

                      <p>
                        Manage your working week without unnecessary clutter.
                      </p>
                    </div>
                  </li>

                  <li>
                    <span>
                      <Check2 />
                    </span>

                    <div>
                      <strong>Client self-booking</strong>

                      <p>
                        Let selected clients book from the times you make
                        available.
                      </p>
                    </div>
                  </li>

                  <li>
                    <span>
                      <ShieldCheck />
                    </span>

                    <div>
                      <strong>Simple from the beginning</strong>

                      <p>
                        Guided setup helps you configure your diary in
                        minutes.
                      </p>
                    </div>
                  </li>
                </ul>

                <div className="login-price">
                  <div>
                    <strong>£11.99</strong>
                    <span>per month</span>
                  </div>

                  <small>
                    International customers pay the equivalent in their local
                    currency.
                  </small>
                </div>

                <Link to="/signup" className="login-signup-button">
                  Start your 1-month free trial
                  <ArrowRight />
                </Link>

                <p className="login-signup-note">
                  No credit card required. Cancel at any time.
                </p>
              </div>
            </aside>
          </div>
        </main>
      </PublicLayout>
    </div>
  );
}